// PATH: src/lib/webauthn.ts
/* eslint-disable */

type BridgeInitResult = {
  status: 'pending';
  callId: number;
};

type BridgePollResult =
  | { status: 'pending' }
  | { success: true; registrationResponseJson?: string; authenticationResponseJson?: string }
  | { success: false; errorCode: string; errorMessage: string };

function isBridgeAvailable(): boolean {
  const bridge = (window as any).AndroidCredential;
  return !!(bridge && typeof bridge.createWebAuthnCredential === 'function' && typeof bridge.getCreateWebAuthnResult === 'function');
}

function patchOptionsForBridge(options: any): any {
  const cloned = JSON.parse(JSON.stringify(options));
  if (cloned.rp) {
    cloned.rp.id = 'app.isentinel360.com';
  } else if (cloned.rpId) {
    cloned.rpId = 'app.isentinel360.com';
  }
  return cloned;
}

async function pollBridgeResult<T>(
  callId: number,
  pollFn: (id: string) => Promise<string>,
  timeoutMs = 30000
): Promise<T> {
  const start = Date.now();
  while (Date.now() - start < timeoutMs) {
    const raw = await pollFn(String(callId));
    const result: BridgePollResult = JSON.parse(raw);
    if ('status' in result && result.status === 'pending') {
      await new Promise(r => setTimeout(r, 200));
      continue;
    }
    if ('success' in result && result.success) {
      return result as T;
    } else if ('success' in result && !result.success) {
      throw new Error(result.errorMessage || 'Bridge operation failed');
    } else {
      throw new Error('Unexpected bridge response');
    }
  }
  throw new Error('Timeout waiting for credential operation');
}

export async function createCredential(options: any): Promise<any> {
  if (isBridgeAvailable()) {
    const bridge = (window as any).AndroidCredential;
    const patchedOptions = patchOptionsForBridge(options);
    const requestJson = JSON.stringify(patchedOptions);
    const initRaw = bridge.createWebAuthnCredential(requestJson);
    const initResult: BridgeInitResult = JSON.parse(initRaw);
    if (initResult.status !== 'pending' || typeof initResult.callId !== 'number') {
      throw new Error('Failed to initiate credential creation');
    }
    const result = await pollBridgeResult<{ registrationResponseJson: string }>(
      initResult.callId,
      bridge.getCreateWebAuthnResult.bind(bridge)
    );
    return JSON.parse(result.registrationResponseJson);
  } else {
    const { startRegistration } = await import('@simplewebauthn/browser');
    return startRegistration({ optionsJSON: options });
  }
}

export async function getCredential(options: any): Promise<any> {
  if (isBridgeAvailable()) {
    const bridge = (window as any).AndroidCredential;
    const patchedOptions = patchOptionsForBridge(options);
    const requestJson = JSON.stringify(patchedOptions);
    const initRaw = bridge.getWebAuthnCredential(requestJson);
    const initResult: BridgeInitResult = JSON.parse(initRaw);
    if (initResult.status !== 'pending' || typeof initResult.callId !== 'number') {
      throw new Error('Failed to initiate credential request');
    }
    const result = await pollBridgeResult<{ authenticationResponseJson: string }>(
      initResult.callId,
      bridge.getGetWebAuthnResult.bind(bridge)
    );
    return JSON.parse(result.authenticationResponseJson);
  } else {
    const { startAuthentication } = await import('@simplewebauthn/browser');
    return startAuthentication({ optionsJSON: options });
  }
}